import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import Icons from '../../../public/static/graphics/processed/enums';
import Chip from '../Chip/Chip';
import { IconColor } from '../Icon/enums';
import InfoPopover from '../InfoPopover/InfoPopover';
import localStyles from './TextChip.module.pcss';

export interface ChipItem {
  /**
   * Label of ChipItem
   */
  label: string;

  /**
   * Value of ChipItem
   */
  value: string;

  /**
   * If ChipItem is valid or not
   */
  valid: boolean;
}

interface Props {
  /**
   * Entire object of item
   */
  item: ChipItem;

  /**
   * When set to true, highlights the chip.
   */
  hasFocus?: boolean;

  /**
   * Class to be applied to the chip
   */
  className?: string;

  /**
   * Callback when a chip is removed.
   *
   * @callback
   * @param item - the id of the chip removed
   */
  onRemove?: (item: ChipItem) => void;
  /**
   * Whether or not to show remove button
   */
  canRemove?: boolean;
  /**
   * The info popover props
   */
  infoPopover?: SelectedItemInfoPopoverProps;
  /**
   * The info danger popover props
   */
  infoDanger?: SelectedItemInfoDangerProps;
}

export interface SelectedItemInfoPopoverProps {
  /**
   * Callback function that adds the info popover based off of properties of the item
   * @param item the item
   */
  showInfoPopoverFn: (item: string) => boolean | null;
  /**
   * Displays a title on hover indicating the info text
   */
  infoText: (item: string) => string;
  /**
   * The class name used for this info popover
   */
  className?: string;
}

export interface SelectedItemInfoDangerProps {
  /**
   * Callback function that adds the info danger icon based off of properties of the item
   * @param item the item
   */
  showInfoDangerFn: (item: string) => boolean | null;
  /**
   * Displays a title on hover indicating the info danger text
   */
  infoDangerText: (item: string) => string;
  /**
   * The class name used for this info danger popover
   */
  className?: string;
}

/**
 * Simple text chip
 * @author Andre Almeida
 */
const TextChip: React.FC<React.PropsWithChildren<Props>> = ({
  item,
  className,
  hasFocus,
  onRemove,
  canRemove = true,
  infoPopover,
  infoDanger
}) => {
  const cx = useClassNameMapper(localStyles);
  const { showInfoPopoverFn, infoText, className: infoPopoverClassName } = infoPopover || {};
  const { showInfoDangerFn, infoDangerText, className: infoDangerClassName } = infoDanger || {};

  return (
    <Chip<ChipItem>
      className={cx(
        className,
        'lia-chip',
        {
          'lia-has-focus': hasFocus,
          'lia-is-invalid': !item?.valid
        },
        infoDanger && { 'lia-g-pr-10': showInfoDangerFn(item.value) }
      )}
      type={item}
      Content={(): JSX.Element => (
        <>
          <span data-testid={`TextChip.${item.value}`} className={cx('lia-text', 'lia-g-mr-10')}>
            {item.label}
          </span>
          {infoPopover && showInfoPopoverFn(item.value) && (
            <InfoPopover
              infoText={infoText(item.value)}
              btnClassName={cx('lia-g-ml-5 align-middle')}
              className={cx(infoPopoverClassName)}
            />
          )}
          {infoDanger && showInfoDangerFn(item.value) && (
            <InfoPopover
              infoText={infoDangerText(item.value)}
              btnClassName={cx('lia-g-ml-5 align-middle')}
              className={cx(infoDangerClassName)}
              icon={Icons.DangerIcon}
              iconColor={IconColor.DANGER}
            />
          )}
        </>
      )}
      onRemove={canRemove ? (): void => onRemove(item) : null}
    />
  );
};

export default TextChip;
